/**
 * Income Tax Controller (EPIC-025 / 25.5 — §14).
 *
 * Page : `/revenus.html`. Pilote l'affichage du récap fiscal annuel
 * pour la déclaration 2042-C-PRO :
 *   - chargement initial pour l'année N-1
 *     (GET /income-tax/yearly-summary?year=YYYY)
 *   - navigation par exercice (bornes : `organizationCreatedYear` ↔ N-1)
 *   - téléchargement PDF via `window.open` (GET .../pdf)
 *   - marquage comme fait via modale + Idempotency-Key (POST .../mark-done)
 *
 * Deux blocs distincts :
 *   - « Revenus à déclarer » : activité, case 2042-C-PRO, montant à
 *     saisir (CA brut) — l'actionnable.
 *   - « Détail du revenu net imposable » : CA brut, abattement, revenu
 *     net — information pour anticiper, sans case.
 *
 * Conventions :
 *   - aucun HTML inline en JS : `<template>` + `data-slot` (cf.
 *     `utils/template.js`)
 *   - seules les activités avec du CA sont affichées (l'API ne renvoie
 *     que les catégories à CA > 0, déjà ordonnées vente → services → BNC)
 *   - les erreurs 409 (déjà marqué) sont traitées comme un succès
 *     idempotent : refresh silencieux
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const CATEGORY_LABELS = {
  bicVente: "BIC vente de marchandises",
  bicServices: "BIC prestations de services",
  bnc: "BNC professions libérales",
};

const CATEGORY_ALLOWANCE_RATES = {
  bicVente: 71,
  bicServices: 50,
  bnc: 34,
};

// Cases 2042-C-PRO par catégorie selon le régime (miroir de
// IncomeTaxService::TAX_FORM_FIELDS). La case d'une ligne active vient
// toujours de l'API (`row.taxFormField`) ; cette table sert au libellé
// de référence en sidebar et de repli défensif.
const CATEGORY_TAX_FORM_FIELDS = {
  withoutVl: { bicVente: "5KO", bicServices: "5KP", bnc: "5HQ" },
  withVl: { bicVente: "5TA", bicServices: "5TB", bnc: "5TE" },
};

export default class IncomeTaxController extends BaseController {
  static targets = [
    "crumbYear",
    "overlineYear",
    "title",
    "pageSub",
    "prevYearButton",
    "nextYearButton",
    "markDoneButton",
    "markedBadge",

    "statsRegion",
    "grossLabel",
    "grossValue",
    "allowanceValue",
    "allowanceDescription",
    "netValue",
    "netDescription",
    "vlValue",
    "vlDescription",

    "declareIntro",
    "declareTableBody",
    "declareHint",

    "detailIntro",
    "caHeaderYear",
    "detailTableBody",
    "detailTableFoot",

    "pdfLink",

    "confirmModal",
    "confirmYear",
    "confirmSubmitButton",
  ];

  connect() {
    this._summary = null;
    this._year = new Date().getFullYear() - 1;
    this._minYear = this._year;
    this._maxYear = this._year;
    this._markInFlight = false;
    this.loadYear(this._year);
  }

  // ---------------------------------------------------------------------------
  // Chargement & rendu
  // ---------------------------------------------------------------------------

  async loadYear(year) {
    try {
      const response = await api.incomeTax.getYearlySummary(year);
      this._summary = response;
      this._year = Number(response.year ?? year);
      const orgYear = Number(response.organizationCreatedYear ?? this._year);
      this._minYear = Number.isFinite(orgYear) ? orgYear : this._year;
      this._maxYear = new Date().getFullYear() - 1;
      this._render();
    } catch (error) {
      this._handleLoadError(error, year);
    }
  }

  _render() {
    const year = this._year;
    // Le versement libératoire est actif ssi l'API renvoie un montant VL
    // (null quand l'option n'est pas souscrite — cf. IncomeTaxService).
    this._vlActive = this._summary?.totals?.liberativePaymentAmount != null;

    this.crumbYearTarget.textContent = `Aide à la déclaration de revenus ${year}`;
    this.overlineYearTarget.textContent = `Récap pour la 2042-C-PRO — exercice ${year}`;
    this.titleTarget.textContent = `Déclaration de revenus ${year}`;
    this.caHeaderYearTarget.textContent = `CA brut ${year}`;
    this.grossLabelTarget.textContent = `CA brut ${year}`;

    this._renderNavigation();
    this._renderStats();
    this._renderDeclareTable();
    this._renderDetailTable();
    this._renderDeclareHint();
    this._renderMarkedState();
  }

  // Case 2042-C-PRO d'une catégorie selon le régime VL en cours.
  _caseFor(category) {
    const map = this._vlActive
      ? CATEGORY_TAX_FORM_FIELDS.withVl
      : CATEGORY_TAX_FORM_FIELDS.withoutVl;
    return map[category] ?? "—";
  }

  // Nom(s) d'activité d'une ligne ; repli sur le libellé de catégorie.
  _activityName(row) {
    const labels = row.activityLabels ?? [];
    return labels.length ? labels.join(", ") : (CATEGORY_LABELS[row.category] ?? row.category);
  }

  // Régime fiscal affiché en sous-titre ; vide si déjà rendu comme nom.
  _regimeLabel(row) {
    const labels = row.activityLabels ?? [];
    return labels.length ? (CATEGORY_LABELS[row.category] ?? "") : "";
  }

  _renderNavigation() {
    const prev = this._year - 1;
    const next = this._year + 1;
    this.prevYearButtonTarget.textContent = `← Exercice ${prev}`;
    this.nextYearButtonTarget.textContent = `Exercice ${next} →`;

    const prevDisabled = prev < this._minYear;
    const nextDisabled = next > this._maxYear;
    this.prevYearButtonTarget.disabled = prevDisabled;
    this.prevYearButtonTarget.setAttribute("aria-disabled", prevDisabled ? "true" : "false");
    this.nextYearButtonTarget.disabled = nextDisabled;
    this.nextYearButtonTarget.setAttribute("aria-disabled", nextDisabled ? "true" : "false");
  }

  _renderStats() {
    const s = this._summary;
    const totals = s.totals ?? {};
    const gross = Number(totals.grossRevenue ?? 0);
    const allowance = Number(totals.allowanceAmount ?? 0);
    const net = Number(totals.netTaxableIncome ?? 0);
    const vl = totals.liberativePaymentAmount;

    this.grossValueTarget.textContent = "";
    this.grossValueTarget.appendChild(document.createTextNode(`${this._formatInteger(gross)} `));
    this.grossValueTarget.appendChild(this._buildEuroSmall());

    this.allowanceValueTarget.textContent = "";
    this.allowanceValueTarget.appendChild(document.createTextNode(`${this._formatInteger(allowance)} `));
    this.allowanceValueTarget.appendChild(this._buildEuroSmall());
    this.allowanceDescriptionTarget.textContent = this._buildAllowanceDescription();

    this.netValueTarget.textContent = "";
    this.netValueTarget.appendChild(document.createTextNode(`${this._formatInteger(net)} `));
    this.netValueTarget.appendChild(this._buildEuroSmall());
    this.netDescriptionTarget.textContent = this._buildNetDescription();

    this.vlValueTarget.textContent = "";
    if (vl != null) {
      this.vlValueTarget.appendChild(document.createTextNode(`${this._formatInteger(Number(vl))} `));
      this.vlValueTarget.appendChild(this._buildEuroSmall());
      this.vlDescriptionTarget.textContent = "Acquitté via l'URSSAF";
    } else {
      this.vlValueTarget.appendChild(document.createTextNode("— "));
      this.vlValueTarget.appendChild(this._buildEuroSmall());
      this.vlDescriptionTarget.textContent = "Non opté";
    }
  }

  // Lignes actives (catégories avec du CA), dans l'ordre figé de l'API.
  _activeRows() {
    return this._summary.byCategory ?? [];
  }

  // Bloc 1 « Revenus à déclarer » : ce qu'on saisit littéralement sur la
  // déclaration — activité, case 2042-C-PRO, CA brut (euros pleins).
  _renderDeclareTable() {
    this.declareTableBodyTarget.innerHTML = "";
    const rows = this._activeRows();

    if (rows.length === 0) {
      const empty = this.cloneTemplate("income-tax-declare-empty-template");
      this.declareTableBodyTarget.appendChild(empty);
      return;
    }

    for (const row of rows) {
      const fragment = this.cloneTemplate("income-tax-declare-row-template");
      this.fillSlots(fragment, {
        activityName: this._activityName(row),
        regimeLabel: this._regimeLabel(row),
        taxFormField: row.taxFormField || this._caseFor(row.category),
        declareAmount: this._formatEuro0(Number(row.grossRevenue)),
      });
      this.declareTableBodyTarget.appendChild(fragment);
    }
  }

  // Bloc 2 « Détail du revenu net imposable » : stats d'anticipation
  // (CA brut, abattement, revenu net), sans la case — ce n'est pas à
  // reporter, l'administration le recalcule.
  _renderDetailTable() {
    this.detailTableBodyTarget.innerHTML = "";
    this.detailTableFootTarget.innerHTML = "";
    const rows = this._activeRows();

    if (rows.length === 0) {
      const empty = this.cloneTemplate("income-tax-detail-empty-template");
      this.detailTableBodyTarget.appendChild(empty);
      return;
    }

    let totalNet = 0;
    for (const row of rows) {
      const net = Number(row.netTaxableIncome);
      totalNet += net;

      const fragment = this.cloneTemplate("income-tax-detail-row-template");
      this.fillSlots(fragment, {
        activityName: this._activityName(row),
        regimeLabel: this._regimeLabel(row),
        grossRevenue: this._formatEuro(Number(row.grossRevenue)),
        allowanceRate: `${CATEGORY_ALLOWANCE_RATES[row.category] ?? "—"} %`,
        netTaxableIncome: this._formatEuro(net),
      });
      this.detailTableBodyTarget.appendChild(fragment);
    }

    // Stat « Abattement » : précise la catégorie quand le ME est mono-activité.
    if (rows.length === 1) {
      const only = rows[0].category;
      this.allowanceDescriptionTarget.textContent =
        `${CATEGORY_ALLOWANCE_RATES[only] ?? "—"} % ${CATEGORY_LABELS[only] ?? only}`;
    }

    const totalFragment = this.cloneTemplate("income-tax-detail-total-template");
    this.fillSlots(totalFragment, { total: this._formatEuro(totalNet) });
    this.detailTableFootTarget.appendChild(totalFragment);
  }

  // Callout sous le bloc 1, adapté au régime (textContent — invariant
  // « pas de HTML inline en JS »).
  _renderDeclareHint() {
    this.declareHintTarget.textContent = this._vlActive
      ? "Votre impôt sur le revenu est déjà payé avec vos cotisations URSSAF. Reportez tout de même ces montants dans les cases indiquées : ils servent au calcul de votre revenu fiscal de référence."
      : "Saisissez chaque montant (votre CA brut) dans la case indiquée — l'administration applique l'abattement forfaitaire automatiquement.";
    this.declareIntroTarget.textContent = this._vlActive
      ? "Vous avez opté pour le versement libératoire. Voici les cases et montants à reporter sur votre 2042-C-PRO (section dédiée au versement libératoire)."
      : "Voici exactement quoi saisir sur votre déclaration 2042-C-PRO : pour chaque activité, la case et le montant à reporter.";
  }

  _renderMarkedState() {
    const marked = this._summary?.declaredForYear === true;
    this.markDoneButtonTarget.disabled = marked;
    this.markDoneButtonTarget.setAttribute("aria-disabled", marked ? "true" : "false");
    this.markedBadgeTarget.hidden = !marked;
  }

  // ---------------------------------------------------------------------------
  // Navigation par exercice
  // ---------------------------------------------------------------------------

  previousYear() {
    const target = this._year - 1;
    if (target < this._minYear) return;
    this.loadYear(target);
  }

  nextYear() {
    const target = this._year + 1;
    if (target > this._maxYear) return;
    this.loadYear(target);
  }

  // ---------------------------------------------------------------------------
  // Téléchargement PDF — ouverture nouvel onglet, pas de fetch binaire
  // ---------------------------------------------------------------------------

  downloadPdf(event) {
    if (event) event.preventDefault();
    window.open(api.incomeTax.pdfUrl(this._year), "_blank", "noopener");
  }

  // ---------------------------------------------------------------------------
  // Marquage « fait » — modale de confirmation + idempotency
  // ---------------------------------------------------------------------------

  openMarkDoneConfirm() {
    if (this._summary?.declaredForYear === true) return;
    this.confirmYearTarget.textContent = String(this._year);
    if (typeof this.confirmModalTarget.showModal === "function") {
      this.confirmModalTarget.showModal();
    } else {
      this.confirmModalTarget.setAttribute("open", "");
    }
  }

  cancelMarkDone() {
    if (typeof this.confirmModalTarget.close === "function") {
      this.confirmModalTarget.close();
    } else {
      this.confirmModalTarget.removeAttribute("open");
    }
  }

  async submitMarkDone() {
    if (this._markInFlight) return;
    this._markInFlight = true;
    this.confirmSubmitButtonTarget.disabled = true;

    const idempotencyKey = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    try {
      await api.incomeTax.markDone(this._year, idempotencyKey);
      this.cancelMarkDone();
      await this.loadYear(this._year);
      this.showSuccess("Exercice marqué comme déclaré.");
    } catch (error) {
      this.cancelMarkDone();
      if (error instanceof APIError && error.status === 409) {
        await this.loadYear(this._year);
        this.showSuccess("Cet exercice est déjà marqué comme déclaré.");
      } else {
        this.showError(this._extractMessage(error, "Impossible de marquer cet exercice comme fait."));
      }
    } finally {
      this._markInFlight = false;
      this.confirmSubmitButtonTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _buildEuroSmall() {
    const small = document.createElement("small");
    small.textContent = "€";
    return small;
  }

  _buildAllowanceDescription() {
    const rows = this._summary?.byCategory ?? [];
    if (rows.length === 1) {
      const cat = rows[0].category;
      return `${CATEGORY_ALLOWANCE_RATES[cat] ?? "—"} % ${CATEGORY_LABELS[cat] ?? cat}`;
    }
    if (rows.length > 1) {
      return "Activité mixte — taux appliqué par catégorie";
    }
    return "Aucun abattement (CA nul)";
  }

  _buildNetDescription() {
    const rows = this._summary?.byCategory ?? [];
    if (rows.length === 1 && rows[0].taxFormField) {
      return `À reporter case ${rows[0].taxFormField}`;
    }
    if (rows.length > 1) {
      return "À reporter sur la 2042-C-PRO (détail ci-dessous)";
    }
    return "Rien à reporter";
  }

  _handleLoadError(error, year) {
    this.declareTableBodyTarget.innerHTML = "";
    this.detailTableBodyTarget.innerHTML = "";
    this.detailTableFootTarget.innerHTML = "";
    const declareEmpty = this.cloneTemplate("income-tax-declare-empty-template");
    this.fillSlots(declareEmpty, { message: `Impossible de charger le récap fiscal ${year}.` });
    this.declareTableBodyTarget.appendChild(declareEmpty);
    const detailEmpty = this.cloneTemplate("income-tax-detail-empty-template");
    this.fillSlots(detailEmpty, { message: `Impossible de charger le récap fiscal ${year}.` });
    this.detailTableBodyTarget.appendChild(detailEmpty);
    this.showError(this._extractMessage(error, `Impossible de charger l'exercice ${year}.`));
  }

  _extractMessage(error, fallback) {
    if (error instanceof APIError) {
      return error.message || fallback;
    }
    return error?.message || fallback;
  }

  _formatEuro(amount) {
    const value = Number(amount ?? 0);
    return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }

  // Euros pleins — montant tel qu'on le saisit dans une case 2042-C-PRO.
  _formatEuro0(amount) {
    const value = Number(amount ?? 0);
    return `${value.toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €`;
  }

  _formatInteger(amount) {
    const value = Number(amount ?? 0);
    return value.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
  }
}
